import React from "react";
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VisuallyHidden,
} from "@chakra-ui/react";
import { FiCheckCircle, FiFileText, FiRotateCcw, FiUsers } from "react-icons/fi";

import { useQuery } from "@tanstack/react-query";

import { axiosClient } from "../../client/http-client";
import { getAuthToken } from "../../client/token-utils";
import {
  DataTable,
  DefinitionList,
  EmptyState,
  MotionReveal,
  StatusBadge,
} from "../ui";

interface ApplicationViewModalProps {
  applicationId: any;
  isOpen: boolean;
  onClose: () => void;
}

export type Root = {
  id: number;
  account_id: string;
  branch_id: string;
  application_id: string;
  status: string;
  type: string;
  branch: {
    branch_id: string;
    branch_name: string;
  };
  data: {
    general: {
      type: string;
      label: string;
      data: {
        outlet_legal_name: string;
        outlet_trade_name: string;
        outlet_group_name: string;
        billing_outlet_address: string;
        billing_country: string;
        billing_city: string;
        billing_phone: string;
        billing_po_box: string;
        billing_nearest_landmark: string;
        delivery_outlet_address: string;
        delivery_country: string;
        delivery_city: string;
        delivery_phone: string;
        delivery_po_box: string;
        delivery_nearest_landmark: string;
        business_sector: string;
        website_url: string;
        service_years: number;
      };
    };
    license: {
      type: string;
      label: string;
      data: {
        license_number: string;
        vat_number: string;
        vat_expiration: string;
        license_expiration: string;
      };
    };
    contacts: {
      type: string;
      label: string;
      data: Array<{
        title: string;
        name: string;
        email: string;
        phone: string;
        mobile: string;
        shareholder_percentage: string;
      }>;
    };
    requests: {
      type: string;
      label: string;
      data: {
        credit_limit: string;
        credit_period: string;
        payment_mode: any;
      };
    };
    documents: {
      type: string;
      label: string;
      data: Array<{
        id: number;
        fieldname: string;
        filename: string;
        originalname: string;
        mimetype: string;
        path: string;
        size: string;
      }>;
    };
  };
};

type Contact = Root["data"]["contacts"]["data"][number];
type UploadedDocument = Root["data"]["documents"]["data"][number];

/** Human titles for the upload field names (falls back to the raw fieldname). */
const DOCUMENT_LABELS: Record<string, string> = {
  license: "Trade licence",
  owner_pp: "Owner/Partner passport",
  owner_eid: "Owner/Partner EID",
  owner_visa: "Owner visa",
  power_of_attorney: "Power of attorney",
  vat: "VAT certificate",
};

/** h3 (xs look) + hairline section, fading up with a ≤ 40 ms stagger (§7.4). */
const Section: React.FC<{
  title: string;
  index: number;
  children: React.ReactNode;
}> = ({ title, index, children }) => (
  <MotionReveal as="section" delay={index * 0.04}>
    <Heading as="h3" size="xs">
      {title}
    </Heading>
    <Divider mt={3} mb={4} />
    {children}
  </MotionReveal>
);

/** Definition-row skeleton in the real section rhythm — never a spinner (§7.0). */
const ViewSkeleton: React.FC = () => (
  <Stack spacing={8} aria-busy="true">
    <VisuallyHidden>Loading</VisuallyHidden>
    {[0, 1, 2].map((section) => (
      <Box key={section}>
        <Skeleton speed={1.2} h="1rem" w="10rem" mb={3} />
        <Divider mb={4} />
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
          columnGap={6}
          rowGap={4}
        >
          {[70, 50, 60, 45].map((width, i) => (
            <Box key={i}>
              <Skeleton speed={1.2} h="0.625rem" w="35%" mb={2} />
              <Skeleton speed={1.2} h="0.875rem" w={`${width}%`} />
            </Box>
          ))}
        </Grid>
      </Box>
    ))}
  </Stack>
);

export const RegisterBranchApplicationViewModal: React.FC<
  ApplicationViewModalProps
> = ({ applicationId, isOpen, onClose }) => {
  const token = getAuthToken();

  // get Selected application data
  const getSelectedApplication = async (applicationId: string) => {
    const response = await axiosClient.get(
      `/applications/${applicationId}/registerbranch`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response;
  };

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ["selectedApplication", applicationId],
    queryFn: () => getSelectedApplication(applicationId),
    enabled: !!applicationId,
  });

  const application = data?.data as Root | undefined;
  const general = application?.data?.general?.data;
  const license = application?.data?.license?.data;
  const contacts: Contact[] = application?.data?.contacts?.data ?? [];
  const requests = application?.data?.requests?.data;
  const documents: UploadedDocument[] =
    application?.data?.documents?.data ?? [];
  const title =
    general?.outlet_legal_name || application?.application_id || "Branch application";
  const status = application?.status;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={"3xl"}
      scrollBehavior="inside"
      motionPreset="slideInBottom"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text textStyle="label" color="text.muted" aria-hidden>
            APPLICATION · REGISTER BRANCH
          </Text>
          <Flex
            align="center"
            gap={3}
            wrap="wrap"
            mt={1}
            pr={{ base: 8, md: 6 }}
          >
            {isPending ? (
              <Skeleton speed={1.2} h="1.5rem" w="12rem" />
            ) : (
              <>
                <Box as="h2" noOfLines={1} minW={0}>
                  {title}
                </Box>
                {status && <StatusBadge kind="application" value={status} />}
              </>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton aria-label="Close" />
        <ModalBody>
          {isPending ? (
            <ViewSkeleton />
          ) : isError ? (
            <Alert status="error" alignItems="flex-start">
              <AlertIcon />
              <Box flex="1">
                <AlertTitle>We couldn't load this application</AlertTitle>
              </Box>
              <Button variant="ghost" size="sm" leftIcon={<FiRotateCcw />} onClick={() => refetch()}>
                Retry
              </Button>
            </Alert>
          ) : (
            <Stack spacing={8}>
              <Section index={0} title="General information">
                <Stack spacing={5}>
                  <DefinitionList
                    columns={2}
                    items={[
                      {
                        label: "Outlet Legal Name",
                        value: general?.outlet_legal_name,
                      },
                      {
                        label: "Outlet Trade Name",
                        value: general?.outlet_trade_name,
                      },
                      {
                        label: "Outlet Group Name",
                        value: general?.outlet_group_name,
                      },
                      {
                        label: "Business Sector",
                        value: general?.business_sector,
                      },
                      { label: "Website URL", value: general?.website_url },
                      { label: "Service Years", value: general?.service_years },
                    ]}
                  />
                  <DefinitionList
                    eyebrow="BILLING ADDRESS"
                    columns={2}
                    items={[
                      {
                        label: "Billing Outlet Address",
                        value: general?.billing_outlet_address,
                      },
                      {
                        label: "Billing Country",
                        value: general?.billing_country,
                      },
                      { label: "Billing City", value: general?.billing_city },
                      { label: "Billing Phone", value: general?.billing_phone },
                      {
                        label: "Billing PO Box",
                        value: general?.billing_po_box,
                      },
                      {
                        label: "Billing Nearest Landmark",
                        value: general?.billing_nearest_landmark,
                      },
                    ]}
                  />
                  <DefinitionList
                    eyebrow="DELIVERY ADDRESS"
                    columns={2}
                    items={[
                      {
                        label: "Delivery Outlet Address",
                        value: general?.delivery_outlet_address,
                      },
                      {
                        label: "Delivery Country",
                        value: general?.delivery_country,
                      },
                      {
                        label: "Delivery City",
                        value: general?.delivery_city,
                      },
                      {
                        label: "Delivery Phone",
                        value: general?.delivery_phone,
                      },
                      {
                        label: "Delivery PO Box",
                        value: general?.delivery_po_box,
                      },
                      {
                        label: "Delivery Nearest Landmark",
                        value: general?.delivery_nearest_landmark,
                      },
                    ]}
                  />
                </Stack>
              </Section>

              <Section index={1} title="Licence information">
                <DefinitionList
                  columns={2}
                  items={[
                    { label: "Licence Number", value: license?.license_number },
                    {
                      label: "Licence Expiration",
                      value: license?.license_expiration,
                    },
                    { label: "VAT Number", value: license?.vat_number },
                    { label: "VAT Expiration", value: license?.vat_expiration },
                  ]}
                />
              </Section>

              <Section index={2} title="Contacts">
                {contacts.length > 0 ? (
                  <DataTable size="sm" label="Contacts table">
                    <Table>
                      <Thead>
                        <Tr>
                          <Th scope="col">Title</Th>
                          <Th scope="col">Name</Th>
                          <Th scope="col">Email</Th>
                          <Th scope="col">Phone</Th>
                          <Th scope="col">Mobile</Th>
                          <Th scope="col" isNumeric>
                            Shareholder Percentage
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {contacts.map((user, index) => (
                          <Tr key={index}>
                            <Td>{user.title}</Td>
                            <Td fontWeight={600}>{user.name}</Td>
                            <Td>{user.email}</Td>
                            <Td fontWeight={600}>{user.phone}</Td>
                            <Td fontWeight={600}>{user.mobile}</Td>
                            <Td isNumeric>{user.shareholder_percentage}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </DataTable>
                ) : (
                  <EmptyState icon={FiUsers} title="No contacts added" py={6} headingAs="p" />
                )}
              </Section>

              <Section index={3} title="Credit request">
                <DefinitionList
                  columns={2}
                  items={[
                    { label: "Credit Limit", value: requests?.credit_limit },
                    { label: "Credit Period", value: requests?.credit_period },
                    { label: "Payment Mode", value: requests?.payment_mode },
                  ]}
                />
              </Section>

              <Section index={4} title="Documents">
                {documents.length > 0 ? (
                  <Grid
                    templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                    gap={3}
                  >
                    {documents.map((document) => (
                      <HStack
                        key={document.id}
                        spacing={3}
                        p={3}
                        borderRadius="md"
                        bg="surface.sunken"
                        minW={0}
                      >
                        <Flex
                          w="36px"
                          h="36px"
                          flexShrink={0}
                          borderRadius="full"
                          bg="leaf.100"
                          color="leaf.700"
                          align="center"
                          justify="center"
                          aria-hidden
                        >
                          <Icon as={FiFileText} boxSize="16px" />
                        </Flex>
                        <Box flex={1} minW={0}>
                          <Text textStyle="body.sm" fontWeight={700} noOfLines={1}>
                            {DOCUMENT_LABELS[document.fieldname] ??
                              document.fieldname}
                          </Text>
                          <Text
                            textStyle="caption"
                            color="text.muted"
                            noOfLines={1}
                            title={document.originalname}
                          >
                            {document.originalname}
                          </Text>
                        </Box>
                        <Icon
                          as={FiCheckCircle}
                          boxSize="16px"
                          color="leaf.600"
                          flexShrink={0}
                          aria-hidden
                        />
                        <VisuallyHidden>Uploaded</VisuallyHidden>
                      </HStack>
                    ))}
                  </Grid>
                ) : (
                  <EmptyState
                    icon={FiFileText}
                    title="No documents uploaded"
                    py={6}
                    headingAs="p"
                  />
                )}
              </Section>
            </Stack>
          )}
        </ModalBody>

        <ModalFooter justifyContent="flex-end">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
